import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { DollarSign, CreditCard, FileText, TrendingUp, Calendar, Download, Plus } from "lucide-react";

export default function AdvertiserBilling() {
  const { advertiser, advertiserId } = useOutletContext();
  const { token } = useAuth();
  const [balance, setBalance] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBillingData();
  }, [advertiserId]);

  const fetchBillingData = async () => {
    try {
      const [balanceRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/billing/balance?account_id=${advertiserId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { balance: 0, currency: "AUD" } })),
        axios.get(`${API}/billing/invoices?account_id=${advertiserId}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setBalance(balanceRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      toast.error("Failed to fetch billing data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      PAID: "bg-green-500",
      PENDING: "bg-yellow-500",
      OVERDUE: "bg-red-500",
      DRAFT: "bg-gray-500",
    };
    return (
      <Badge variant="outline">
        <span className={`w-2 h-2 rounded-full ${colors[status] || 'bg-gray-500'} mr-1.5`}></span>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-primary">Loading...</div></div>;
  }

  // Calculate totals
  const totalSpend = invoices.filter(i => i.status === 'PAID').reduce((sum, i) => sum + (i.total || 0), 0);
  const pendingAmount = invoices.filter(i => i.status === 'PENDING').reduce((sum, i) => sum + (i.total || 0), 0);

  return (
    <div className="space-y-6">
      {/* Balance & Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-green-500/10">
                <DollarSign className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Current Balance</p>
                <p className="text-2xl font-bold">${balance?.balance?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-blue-500/10">
                <TrendingUp className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Spend</p>
                <p className="text-2xl font-bold">${totalSpend.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-yellow-500/10">
                <FileText className="h-6 w-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">${pendingAmount.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-purple-500/10">
                <CreditCard className="h-6 w-6 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Invoices</p>
                <p className="text-2xl font-bold">{invoices.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Methods Placeholder */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              Payment Methods
            </h3>
            <Button variant="outline" size="sm" disabled>
              <Plus className="mr-2 h-4 w-4" />
              Add Payment Method
            </Button>
          </div>
          <div className="border border-dashed border-border rounded-lg p-8 text-center">
            <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Stripe integration coming soon</p>
            <p className="text-sm text-muted-foreground mt-1">Payment methods will be managed here</p>
          </div>
        </CardContent>
      </Card>

      {/* Invoices */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Invoices
          </h3>
          
          {invoices.length > 0 ? (
            <div className="divide-y divide-border">
              {invoices.map(invoice => (
                <div key={invoice.id} className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-muted">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium">Invoice #{invoice.invoice_number || invoice.id?.slice(0, 8)}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(invoice.created_at || invoice.date).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {getStatusBadge(invoice.status)}
                    <span className="font-semibold">${invoice.total?.toLocaleString() || 0}</span>
                    <Button variant="ghost" size="sm" disabled>
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-dashed border-border rounded-lg p-8 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No invoices yet</p>
              <p className="text-sm text-muted-foreground mt-1">Invoices will appear here once campaigns are running</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
